/**
 * Post Shared Preview Render
 *
 * Rendering helpers for shared post preview sections, titles, ratings, and review answers.
 */

import { Roles } from "../../constants/roles";
import { CommonUtils } from "../../util/common-utils";
import { postSharedPreviewForm } from "./post-shared-preview-form";

const HtmlInsertPosition = {
    BEFORE_END: "beforeend" as InsertPosition,
    AFTER_BEGIN: "afterbegin" as InsertPosition,
};

interface RenderPreviewTitleParams {
    previewBody: HTMLElement;
    titleValue: string;
}

interface ReviewQuestionsPreviewHtmlParams {
    rowsHtml: string[];
    ratingCount: number;
    averageStarsHtml: string;
    averageText: string;
}

interface AttachmentsPreviewGridParams {
    previewRoot: HTMLElement;
    formRoot: HTMLElement;
}

function isMarkedRemovedTile(tile: HTMLElement): boolean {
    const removedIndicator = postSharedPreviewForm.getMediaRemovedIndicator(tile);
    const controls = postSharedPreviewForm.getMediaControls(tile);

    return (
        tile.style.opacity === "0.5" ||
        (removedIndicator instanceof HTMLElement && removedIndicator.style.display === "flex") ||
        (controls instanceof HTMLElement && controls.style.display === "none")
    );
}

class PostSharedPreviewRender {
    public static ensureRichHost(previewRoot: HTMLElement): HTMLElement {
        let richHostCandidate = postSharedPreviewForm.getPreviewRichHost(previewRoot);

        if (!(richHostCandidate instanceof HTMLElement)) {
            previewRoot.insertAdjacentHTML(
                HtmlInsertPosition.BEFORE_END,
                `<div class="rich-text-preview" data-role="${Roles.PREVIEW_RICH_HOST}"></div>`,
            );
            richHostCandidate = postSharedPreviewForm.getPreviewRichHost(previewRoot);
        }

        return richHostCandidate instanceof HTMLElement ? richHostCandidate : previewRoot;
    }

    public static renderPreviewTitle(params: RenderPreviewTitleParams): void {
        const { previewBody, titleValue } = params;

        const existingTitle = postSharedPreviewForm.getPreviewTitle(previewBody);
        if (existingTitle) {
            existingTitle.remove();
        }

        if (!titleValue || titleValue.startsWith("review:")) {
            return;
        }

        const safeTitle = CommonUtils.escapeHtml(String(titleValue));
        previewBody.insertAdjacentHTML(
            HtmlInsertPosition.AFTER_BEGIN,
            `<div class="preview-title" data-role="${Roles.PREVIEW_TITLE}"><h2>${safeTitle}</h2></div>`,
        );
    }

    public static buildReviewQuestionsPreviewHtml(params: ReviewQuestionsPreviewHtmlParams): string {
        const { rowsHtml, ratingCount, averageStarsHtml, averageText } = params;

        return `
            <div class="review-questions-preview post-detail" data-preview-generated="1" data-role="${Roles.REVIEW_QUESTIONS_PREVIEW}">
                <div class="review-answers-section">
                    <h3 class="review-answers-title">
                        <span class="review-answers-title__left"><span data-translate="review_details">Review Details</span></span>
                        <span class="review-answers-title__right">
                            ${
                                ratingCount > 0
                                    ? `<span class="rating-stars" aria-label="Average rating">${averageStarsHtml}</span><span class="review-average-rating">${averageText}</span>`
                                    : ""
                            }
                        </span>
                    </h3>
                    <div class="review-answers-list">${rowsHtml.join("")}</div>
                </div>
            </div>
        `;
    }

    public static renderAttachmentsPreviewGrid(params: AttachmentsPreviewGridParams): void {
        const { previewRoot, formRoot } = params;

        const imagesGrid = postSharedPreviewForm.getImagesPreviewGrid(previewRoot);
        const videosGrid = postSharedPreviewForm.getVideosPreviewGrid(previewRoot);
        const snsPreviewGrid = postSharedPreviewForm.getSnsPreviewGrid(previewRoot);

        const imageTiles = postSharedPreviewForm.getImageManagerTiles(formRoot);
        const videoGrid = postSharedPreviewForm.getFormVideoGrid(formRoot);
        const snsGrid = postSharedPreviewForm.getFormSnsGrid(formRoot);

        const appendTileClone = (tile: HTMLElement, targetGrid: Element | null): void => {
            if (!targetGrid) return;
            if (
                tile.getAttribute("data-role") === Roles.PICKER_ADD ||
                tile.getAttribute("data-role") === Roles.SNS_ADD_TILE
            )
                return;
            if (isMarkedRemovedTile(tile)) return;

            const clone = tile.cloneNode(true);
            if (!(clone instanceof HTMLElement)) {
                return;
            }

            postSharedPreviewForm.getMediaControlsList(clone).forEach((element) => element.remove());

            targetGrid.appendChild(clone);
        };

        if (imagesGrid) imagesGrid.innerHTML = "";
        if (videosGrid) videosGrid.innerHTML = "";
        if (snsPreviewGrid) snsPreviewGrid.innerHTML = "";

        imageTiles.forEach((tile) => {
            appendTileClone(tile, imagesGrid);
        });

        if (videoGrid) {
            Array.from(videoGrid.children).forEach((child) => {
                if (child instanceof HTMLElement) appendTileClone(child, videosGrid);
            });
        }

        if (snsGrid) {
            Array.from(snsGrid.children).forEach((child) => {
                if (child instanceof HTMLElement) appendTileClone(child, snsPreviewGrid);
            });
        }
    }
}

export { PostSharedPreviewRender };
